import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-sm font-light tracking-wide text-white placeholder:text-white/30 focus:outline-none focus:border-white transition-colors duration-300";

export default function Inquiry() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    service_type: "",
    requests: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Your inquiry has been received. Expect a reply within 24 hours.");
      setForm({ full_name: "", email: "", service_type: "", requests: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email oksana@chefoksana.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="inquiry"
      data-testid="inquiry-section"
      className="px-6 sm:px-12 lg:px-20 py-24 md:py-32 lg:py-40 border-b border-white/10"
    >
      <div className="grid grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-5"
        >
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#EAE6DF]/70 mb-6">
            Inquiries
          </p>
          <h2
            data-testid="inquiry-heading"
            className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mb-10"
          >
            Begin the conversation.
          </h2>
          <p className="text-sm sm:text-base font-light leading-relaxed text-[#F5F5F7]/60 max-w-md mb-14">
            Engagements are limited by design. Share a few details and a private consultation
            will be arranged at your convenience.
          </p>
          <div className="space-y-0 text-sm font-light">
            <div className="border-t border-white/10 py-5 flex justify-between items-center">
              <span className="text-[11px] uppercase tracking-[0.3em] text-white/40">Telephone</span>
              <a
                data-testid="inquiry-phone-link"
                href="tel:+19179945717"
                className="text-white/80 transition-opacity duration-300 hover:opacity-60"
              >
                +1 917 994 5717
              </a>
            </div>
            <div className="border-t border-white/10 py-5 flex justify-between items-center">
              <span className="text-[11px] uppercase tracking-[0.3em] text-white/40">Email</span>
              <a
                data-testid="inquiry-email-link"
                href="mailto:oksana@chefoksana.com"
                className="text-white/80 transition-opacity duration-300 hover:opacity-60"
              >
                oksana@chefoksana.com
              </a>
            </div>
            <div className="border-t border-b border-white/10 py-5 flex justify-between items-center">
              <span className="text-[11px] uppercase tracking-[0.3em] text-white/40">Instagram</span>
              <a
                data-testid="inquiry-instagram-link"
                href="https://bit.ly/4xPsIa7"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 transition-opacity duration-300 hover:opacity-60"
              >
                @chefoksana
              </a>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="inquiry-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-7 lg:pt-24 space-y-12"
        >
          <div>
            <label className="block text-[11px] uppercase tracking-[0.3em] text-white/40 mb-2">
              Full Name
            </label>
            <input
              data-testid="input-full-name"
              type="text"
              required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Your name"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.3em] text-white/40 mb-2">
              Email
            </label>
            <input
              data-testid="input-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="name@example.com"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.3em] text-white/40 mb-2">
              Service Type
            </label>
            <Select
              value={form.service_type}
              onValueChange={(value) => setForm({ ...form, service_type: value })}
              required
            >
              <SelectTrigger
                data-testid="select-service-type"
                className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-sm font-light tracking-wide text-white focus:outline-none focus:border-white focus:ring-0 focus:ring-offset-0 shadow-none"
              >
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border border-white/10 rounded-none text-white">
                <SelectItem data-testid="service-option-placement" value="Long-term Placement" className="rounded-none focus:bg-white/10 focus:text-white">
                  Long-term Placement
                </SelectItem>
                <SelectItem data-testid="service-option-event" value="Private Event" className="rounded-none focus:bg-white/10 focus:text-white">
                  Private Event
                </SelectItem>
                <SelectItem data-testid="service-option-seasonal" value="Seasonal Curation" className="rounded-none focus:bg-white/10 focus:text-white">
                  Seasonal Curation
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.3em] text-white/40 mb-2">
              Bespoke Requests
            </label>
            <textarea
              data-testid="textarea-requests"
              rows={4}
              value={form.requests}
              onChange={(e) => setForm({ ...form, requests: e.target.value })}
              placeholder="Occasion, party size, dietary considerations, timing…"
              className={`${inputClass} resize-none`}
            />
          </div>
          <button
            data-testid="inquiry-submit-button"
            type="submit"
            disabled={submitting}
            className="border border-white/30 px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-white hover:text-[#121212] disabled:opacity-40 disabled:pointer-events-none"
          >
            {submitting ? "Sending…" : "Request Consultation"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
